import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ClipboardList, PackageCheck, Phone, Search, Wallet } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const ORDERS_POLL_INTERVAL_MS = 8000;

const formatRupiah = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatOrderDate = (value) =>
  value
    ? new Date(value).toLocaleString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const statusOptions = ["Pending", "Processing", "Completed", "Cancelled"];
const paymentStatusOptions = ["Pending", "Paid", "Failed", "Refunded"];
const selectClass =
  "h-9 w-full rounded-sm bg-white/[0.04] px-3 text-sm text-white outline-none ring-1 ring-white/[0.035] focus:ring-white/15 disabled:cursor-not-allowed disabled:opacity-60 [&_option]:bg-white [&_option]:text-black";

const statusClass = {
  Pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  Processing: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  Completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  Cancelled: "bg-red-500/10 text-red-400 border-red-500/30",
};

const paymentStatusClass = {
  Pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  Paid: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  Failed: "bg-red-500/10 text-red-400 border-red-500/30",
  Refunded: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

export default function Orders() {
  const { user } = useAuth();
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [orderStatusDraft, setOrderStatusDraft] = useState({});
  const [paymentDraft, setPaymentDraft] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const userRole = String(user?.role || "").trim().toLowerCase();
  const canEditOrders = ["admin", "kasir"].includes(userRole);

  const sortedOrderList = useMemo(
    () =>
      [...orderList].sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();
        return bTime - aTime;
      }),
    [orderList]
  );

  const summary = useMemo(() => {
    const totalOrders = sortedOrderList.length;
    const paidOrders = sortedOrderList.filter((item) => item.payment_status === "Paid").length;
    const processingOrders = sortedOrderList.filter((item) => item.status === "Processing").length;
    const totalRevenue = sortedOrderList
      .filter((item) => item.payment_status === "Paid")
      .reduce((total, item) => total + Number(item.total_amount || 0), 0);

    return {
      totalOrders,
      paidOrders,
      processingOrders,
      totalRevenue,
    };
  }, [sortedOrderList]);

  const filteredOrderList = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return sortedOrderList;

    return sortedOrderList.filter((order) => {
      const searchableText = [
        order.id,
        order.customer_name,
        order.customer_phone,
        order.status,
        order.payment_status,
        order.payment_method,
        order.source,
        ...(order.items || []).map((item) => item.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });
  }, [searchQuery, sortedOrderList]);

  useEffect(() => {
    fetchOrderList();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchOrderList(false);
    }, ORDERS_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  const fetchOrderList = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/shop/orders`, { withCredentials: true });
      const next = Array.isArray(data) ? data : [];
      setOrderList(next);
      setOrderStatusDraft(Object.fromEntries(next.map((item) => [item.id, item.status || "Pending"])));
      setPaymentDraft(Object.fromEntries(next.map((item) => [item.id, item.payment_status || "Pending"])));
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const updateStatus = async (orderId) => {
    if (!canEditOrders) return;
    const nextStatus = orderStatusDraft[orderId];
    const nextPaymentStatus = paymentDraft[orderId];
    if (!nextStatus) return;
    try {
      setSavingId(orderId);
      await axios.put(
        `${API}/shop/orders/${orderId}`,
        { status: nextStatus, payment_status: nextPaymentStatus },
        { withCredentials: true }
      );
      await fetchOrderList(false);
    } catch (error) {
      console.error("Failed to update order status:", error);
      alert(error.response?.data?.detail || "Failed to update order status");
    } finally {
      setSavingId(null);
    }
  };

  const getOrderItemCount = (items) =>
    (items || []).reduce((total, item) => total + Number(item.quantity || 0), 0);

  return (
    <DashboardLayout>
      <Header title="Order" />
      <div className="p-8 space-y-8" data-testid="orders-page">
        <section className="rounded-sm bg-zinc-950/60 p-6 ring-1 ring-white/[0.04]">
          <div className="mb-6">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/[0.04] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-300">
              <ClipboardList className="h-3.5 w-3.5" />
              Order
            </div>
            <h2 className="text-xl font-semibold text-white">Kelola seluruh pesanan masuk</h2>
            <p className="mt-1 text-sm text-zinc-500">
              Pantau pesanan, status pembayaran, dan progres pemrosesan dalam satu halaman.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-sm bg-white/[0.025] p-4 ring-1 ring-white/[0.035]">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Total Order</p>
              <p className="mt-3 text-3xl font-semibold text-white">{summary.totalOrders}</p>
            </div>
            <div className="rounded-sm bg-white/[0.025] p-4 ring-1 ring-white/[0.035]">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sedang Diproses</p>
              <p className="mt-3 text-3xl font-semibold text-white">{summary.processingOrders}</p>
            </div>
            <div className="rounded-sm bg-white/[0.025] p-4 ring-1 ring-white/[0.035]">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Pendapatan Terkonfirmasi</p>
              <p className="mt-3 text-3xl font-semibold text-white">{formatRupiah(summary.totalRevenue)}</p>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm bg-zinc-950/40 ring-1 ring-white/[0.04]">
          <div className="flex flex-col gap-4 border-b border-white/[0.04] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Daftar Order</h3>
              <p className="mt-1 text-sm text-zinc-500">Tampilan ringkas untuk cek customer, total, dan update status dengan cepat.</p>
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cari order..."
                  className="h-10 w-full rounded-sm border-transparent bg-white/[0.04] pl-10 text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15 md:w-72"
                  data-testid="order-search-input"
                />
              </div>
              <div className="hidden items-center gap-4 text-xs uppercase tracking-[0.18em] text-zinc-500 md:flex">
                <span className="inline-flex items-center gap-2">
                  <Wallet className="h-3.5 w-3.5" />
                  Paid: {summary.paidOrders}
                </span>
                <span className="inline-flex items-center gap-2">
                  <PackageCheck className="h-3.5 w-3.5" />
                  Processing: {summary.processingOrders}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-4 p-5 md:p-6">
            {loading ? (
              <div className="py-10 text-center">
                <div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
              </div>
            ) : filteredOrderList.length > 0 ? (
              filteredOrderList.map((order, index) => (
                <article
                  key={order.id || index}
                  className="rounded-sm bg-white/[0.018] p-5 ring-1 ring-white/[0.035] transition-colors hover:bg-white/[0.03]"
                >
                  <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
                    <div className="min-w-0 space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold text-white">{order.customer_name}</p>
                        <Badge variant="outline" className="rounded-sm border-transparent bg-white/[0.04] text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                          {String(order.id || "-").slice(0, 8).toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400">
                        <span className="inline-flex items-center gap-2 rounded-sm bg-white/[0.035] px-3 py-2">
                          <Phone className="h-3.5 w-3.5 text-zinc-500" />
                          {order.customer_phone || "-"}
                        </span>
                        <span className="rounded-sm bg-white/[0.035] px-3 py-2">
                          {formatOrderDate(order.created_at)}
                        </span>
                        <span className="rounded-sm bg-white/[0.035] px-3 py-2">
                          {getOrderItemCount(order.items)} pcs
                        </span>
                      </div>
                      {order.notes ? (
                        <div className="max-w-2xl text-xs leading-5 text-zinc-500">
                          <span className="mr-2 uppercase tracking-[0.18em] text-zinc-600">Catatan</span>
                          {order.notes}
                        </div>
                      ) : null}

                      <div className="grid gap-2">
                        {(order.items || []).slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-4 border-b border-white/[0.04] py-2 text-sm last:border-b-0">
                            <div className="min-w-0">
                              <p className="truncate text-zinc-200">{item.name}</p>
                              <p className="mt-1 text-[11px] text-zinc-500">
                                {Number(item.quantity || 0)} x {formatRupiah(item.unit_price)}
                              </p>
                            </div>
                            <p className="whitespace-nowrap text-xs font-medium text-zinc-400">
                              {formatRupiah(item.line_total)}
                            </p>
                          </div>
                        ))}
                        {(order.items || []).length > 3 && (
                          <p className="pt-1 text-xs text-zinc-500">
                            +{(order.items || []).length - 3} item lain
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4 xl:border-l xl:border-white/[0.04] xl:pl-5">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Total Order</p>
                        <div className="mt-2 flex items-end justify-between gap-3">
                          <p className="text-2xl font-semibold text-white">{formatRupiah(order.total_amount)}</p>
                          <p className="pb-1 text-xs text-zinc-500">{(order.items || []).length} jenis item</p>
                        </div>
                      </div>

                      <div className="grid gap-3">
                        {canEditOrders ? null : (
                          <p className="rounded-sm bg-white/[0.03] px-3 py-2 text-xs text-zinc-500 ring-1 ring-white/[0.04]">
                            User ini belum memiliki akses update order.
                          </p>
                        )}
                        <div className="grid gap-2">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Pembayaran</p>
                            <Badge
                              variant="outline"
                              className={`rounded-sm text-xs ${paymentStatusClass[order.payment_status] || paymentStatusClass.Pending}`}
                            >
                              {order.payment_status || "Pending"}
                            </Badge>
                          </div>
                          <select
                            value={paymentDraft[order.id] || order.payment_status || "Pending"}
                            onChange={(e) =>
                              setPaymentDraft((prev) => ({ ...prev, [order.id]: e.target.value }))
                            }
                            className={selectClass}
                            disabled={!canEditOrders || savingId === order.id}
                          >
                            {paymentStatusOptions.map((paymentStatus) => (
                              <option key={paymentStatus} value={paymentStatus}>
                                {paymentStatus}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="grid gap-2">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Status Order</p>
                            <Badge
                              variant="outline"
                              className={`rounded-sm text-xs ${statusClass[order.status] || statusClass.Pending}`}
                            >
                              {order.status || "Pending"}
                            </Badge>
                          </div>
                          <select
                            value={orderStatusDraft[order.id] || order.status || "Pending"}
                            onChange={(e) =>
                              setOrderStatusDraft((prev) => ({ ...prev, [order.id]: e.target.value }))
                            }
                            className={selectClass}
                            disabled={!canEditOrders || savingId === order.id}
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>

                        <Button
                          onClick={() => updateStatus(order.id)}
                          disabled={!canEditOrders || savingId === order.id}
                          className="h-10 rounded-sm bg-white px-5 font-semibold text-black hover:bg-zinc-200"
                        >
                          {!canEditOrders ? "Tidak dapat diedit" : savingId === order.id ? "Saving..." : "Simpan Update"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div className="rounded-sm bg-white/[0.02] px-4 py-10 text-center text-sm text-zinc-500 ring-1 ring-white/[0.04]">
                {searchQuery.trim() ? "Order tidak ditemukan." : "Belum ada order masuk."}
              </div>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
